// PEV — Gestion des intrants (vaccins, seringues, diluants) : sous-section du module PEV.
// Une colonne « Saisie » par séance de vaccination du mois (avec le « Repère » issu de la fiche de pointage, un clic le reprend),
// puis Total utilisé, Qt périmée, Qt avariée, Stock fin, Contrôle et Taux de perte. Tout est recalculé par calculerModules().
import { PEV_STRATEGIE_LABELS } from '../../../../shared/business-logic/index.ts';
import { PEV_INTRANTS, repereSeance, ligneIntrantPevVide } from '../../../../shared/business-logic/pevIntrants.ts';
import type { IntrantPev } from '../../../../shared/business-logic/pevIntrants.ts';
import type { PevSeance } from '../../../../shared/business-logic/index.ts';
import type { useModules } from '../../hooks/useModules.ts';
import { NombreInput, Message, Pastille } from '../../components/ui.tsx';
import { AFFICHAGE } from '../../config/affichage.ts';

type Modules = ReturnType<typeof useModules>;
type Champ = 'debut' | 'recu' | 'perime' | 'avarie' | 'finSaisie';

const libelleSeance = (d: PevSeance, i: number) => `${d.semaine || `Séance ${i + 1}`}${d.dateSeance ? ` ${d.dateSeance}` : ''} · ${PEV_STRATEGIE_LABELS[d.strategie]}`;

export default function PevIntrants({ m }: { m: Modules }) {
  const calc = m.calc;
  if (!calc) return null;
  const seances = m.saisies('PEV_SEANCE');
  const brut = new Map(m.saisies('PEV_INTRANT').map((l) => [l.cle, l.donnees as Partial<IntrantPev>]));
  const lignes = calc.pev.intrants as Array<{ cle: string; label: string; saisie: Partial<IntrantPev> | null; calcul: any }>;
  const defs = new Map(PEV_INTRANTS.map((d) => [d.key, d]));

  async function ecrire(cle: string, suite: Partial<IntrantPev>) {
    const propre: Partial<IntrantPev> = { ...suite, bySeance: suite.bySeance ?? {} };
    if (ligneIntrantPevVide(propre)) await m.supprimer('PEV_INTRANT', cle); else await m.ecrire('PEV_INTRANT', cle, propre);
  }
  const majChamp = (cle: string, champ: Champ, v: number | null) => ecrire(cle, { ...(brut.get(cle) ?? {}), [champ]: v });
  const majSeance = (cle: string, idSeance: string, v: number | null) => {
    const base = brut.get(cle) ?? {};
    const bySeance = { ...(base.bySeance ?? {}) };
    if (v === null) delete bySeance[idSeance]; else bySeance[idSeance] = v;
    return ecrire(cle, { ...base, bySeance });
  };

  const alertes = lignes.filter((l) => l.calcul.alertePerte).map((l) => `${l.label} ${l.calcul.tauxPerte} %`);
  const incoherents = lignes.filter((l) => !l.calcul.coherent).map((l) => l.label);

  return (
    <section className="carte">
      <h2>Gestion des intrants PEV <Pastille>{lignes.filter((l) => l.saisie).length}/{lignes.length}</Pastille></h2>
      {AFFICHAGE.aideFormulesIntrantsPev && <p className="note">Stock fin = début + reçu − utilisé − périmé − avarié. Le <b>stock début</b> reprend le stock fin du mois précédent ; tapez un stock fin compté pour remplacer le calcul. Cliquez sur un <b>repère</b> pour le reprendre dans la séance.</p>}
      {alertes.length > 0 && <Message niveau="warn">Taux de perte élevé (≥ 10 %) : {alertes.join(' · ')}</Message>}
      {incoherents.length > 0 && <Message niveau="warn">Stock fin saisi différent du calcul : {incoherents.join(', ')}</Message>}
      <div className="defil">
        <table className="saisie compact">
          <thead>
            <tr>
              <th className="fixe" rowSpan={2}>Désignations</th><th rowSpan={2}>Stock début</th><th rowSpan={2}>Qt reçue</th>
              {seances.map((s, i) => <th key={s.cle} colSpan={2}>{libelleSeance(s.donnees as PevSeance, i)}</th>)}
              <th rowSpan={2}>Total utilisé</th><th rowSpan={2}>Qt périmée</th><th rowSpan={2}>Qt avariée</th><th rowSpan={2}>Stock fin</th><th rowSpan={2}>Contrôle</th><th rowSpan={2}>Taux de perte</th>
            </tr>
            <tr>{seances.map((s) => [<th key={`${s.cle}a`}>Saisie</th>, <th key={`${s.cle}b`}>Repère</th>])}</tr>
          </thead>
          <tbody>
            {lignes.map((l) => {
              const c = l.calcul, def = defs.get(l.cle)!, s = brut.get(l.cle) ?? {};
              const mult = (k: string) => (c.multiplesNonRespectes.includes(k) ? 'warn' : '');
              const titreMult = def.multiple ? `Multiple de ${def.multiple} attendu` : undefined;
              return (
                <tr key={l.cle}>
                  <td className="fixe">{l.label}{c.negatif && <small className="bad">Sorties supérieures au stock disponible</small>}</td>
                  <td><NombreInput label={`${l.label} — stock début`} classe={`${c.debutReporte ? 'reporte' : ''} ${mult('debut')}`} titre={c.debutReporte ? 'Reporté du stock fin du mois précédent' : titreMult}
                    valeur={s.debut ?? (c.debutReporte ? c.debut : null)} disabled={m.lectureSeule} onChange={(v) => majChamp(l.cle, 'debut', v)} /></td>
                  <td><NombreInput label={`${l.label} — quantité reçue`} classe={mult('recu')} titre={titreMult} valeur={s.recu} disabled={m.lectureSeule} onChange={(v) => majChamp(l.cle, 'recu', v)} /></td>
                  {seances.map((se) => {
                    const rep = repereSeance(def, se.donnees as PevSeance);
                    return [
                      <td key={`${se.cle}a`}><NombreInput label={`${l.label} — utilisée à la séance ${(se.donnees as PevSeance).semaine}`} valeur={s.bySeance?.[se.cle]} disabled={m.lectureSeule} onChange={(v) => majSeance(l.cle, se.cle, v)} /></td>,
                      <td key={`${se.cle}b`} className="calcule" style={rep !== null && !m.lectureSeule ? { cursor: 'pointer', textDecoration: 'underline dotted' } : undefined}
                        title={rep !== null ? 'Cliquer pour reprendre ce repère' : undefined}
                        onClick={() => { if (rep !== null && !m.lectureSeule) void majSeance(l.cle, se.cle, rep); }}>{rep === null ? '—' : rep}</td>,
                    ];
                  })}
                  <td className={`calcule ${mult('totalUtilise')}`} title={titreMult}><b>{c.totalUtilise}</b></td>
                  <td><NombreInput label={`${l.label} — quantité périmée`} valeur={s.perime} disabled={m.lectureSeule} onChange={(v) => majChamp(l.cle, 'perime', v)} /></td>
                  <td><NombreInput label={`${l.label} — quantité avariée`} valeur={s.avarie} disabled={m.lectureSeule} onChange={(v) => majChamp(l.cle, 'avarie', v)} /></td>
                  <td><NombreInput label={`${l.label} — stock fin`} classe={`${c.finSaisi ? '' : 'lie'} ${mult('fin')} ${c.coherent ? '' : 'bad'}`}
                    titre={c.finSaisi ? `Saisi (calcul : ${c.finCalcule})` : 'Calculé : tapez un stock compté pour le remplacer'}
                    valeur={s.finSaisie ?? c.finCalcule} disabled={m.lectureSeule} onChange={(v) => majChamp(l.cle, 'finSaisie', v)} /></td>
                  <td className={`calcule ${c.coherent ? '' : 'bad'}`}>{c.coherent ? 'OK' : 'Erreur'}</td>
                  <td className={`calcule ${c.alertePerte ? 'warn' : ''}`}>{c.tauxPerte === null ? '—' : `${c.tauxPerte} %`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {seances.length === 0 && <p className="note">Saisissez d'abord les séances de vaccination ci-dessus : chacune devient une colonne du tableau.</p>}
    </section>
  );
}
